import {Component, Input, OnChanges} from '@angular/core';
import {NgIf} from "@angular/common";
import {HighchartsChartModule} from 'highcharts-angular';
import Highcharts from 'highcharts';
import {WIDGET_COLOR_SCALE} from '../../../../../shared/types/common';
import {graphSpec} from '../../../types';
const greyColor = '#4E5258';
const fontFamily = 'Poppins, sans-serif';

@Component({
  selector: 'app-copilot-graphs-message',
  imports: [
    HighchartsChartModule,
    NgIf,
  ],
  templateUrl: './copilot-graphs-message.component.html',
  styleUrl: './copilot-graphs-message.component.scss'
})
export class CopilotGraphsMessageComponent implements OnChanges{
  @Input() data!: graphSpec | undefined;
  protected readonly Highcharts = Highcharts;

  colors = WIDGET_COLOR_SCALE;
  chartOptions!: Highcharts.Options;

  ngOnChanges(): void {
    if (this.data)
      this.chartOptions=this.createHighchartsOptions(this.data);
  }
  private createHighchartsOptions(data: any): Highcharts.Options {
    const formatNumber = (n: any) => {
      const num = Number(n);
      return isNaN(num) ? `${n}` : num.toLocaleString('en-US', {maximumFractionDigits: 20});
    };
    const formatValue = (y: any, unit: string) => {
      const value = formatNumber(y);
      if (unit === 'count') return `${value}`;
      return unit === '$' ? `${unit}${value}` : `${value}${unit}`;
    };
    if (data.type === 'doughnut') {
      return {
        chart: {
          type: 'pie',
          width: 720,
          spacingTop: 20,
          spacingBottom: 0,
          style: {
            fontFamily: fontFamily
          }
        },
        title: {
          text: data.title || '',
          style: {
            fontFamily: fontFamily,
            color: greyColor,
            fontWeight: '600'
          },
        },
        tooltip: {
          useHTML: true,
          backgroundColor: 'rgba(255,255,255,0)',
          borderWidth: 0,
          shadow: false,
          formatter:function () {
              return `<div style="text-align: center;border-radius:4px;box-shadow:0px 0px 6px 0px rgba(0, 0, 0, 0.10);min-width:80px;padding: 4px 8px; margin: 3px; backgroundColor: white">
                        <div style="font-weight:400;font-size:12px;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background-color:${this.color};margin-right:6px;"></span>${this.key}</div>
                        <div style="font-weight:600;font-size:14px;">${formatValue(this.y, (this.options as any)?.unit || '')}</div>
                      </div>`;
            },
          style: {
            fontFamily: fontFamily,
            color: greyColor
          },
        },
        plotOptions: {
          series: {
            animation: false,
          },
          pie: {
            innerSize: '60%',
            borderWidth: 0,
            borderRadius: 0,
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              formatter: function () {
                return `${this.name}: <span style="font-weight: 600">${formatValue(this.y, (this.options as any).unit || '')}</span>`;
              },
              style: {
                fontFamily: fontFamily,
                color: greyColor,
                fontWeight: 'normal',
                fontSize: '12.8px',
                textOutline: '1.5px contrast',
              },
            }
          }
        },
        credits: {
          enabled: false,
        },
        colors: this.colors,
        series: (data.series || []).map((s: any) => ({
          name: s.name,
          colorByPoint: true,
          data: (data.x || []).map((xVal: any, i: any) => ({
            name: xVal,
            y: s.y[i],
            unit: s.unit || ''
          })),
          style: {
            fontFamily: fontFamily,
            color: greyColor
          },
        }))
      };
    }
    return {
      chart: {
        type: data.type || 'bar',
        width: 720,
        spacingTop: 20,
        spacingBottom: 0,
        style: {
          fontFamily: fontFamily
        }
      },
      title: {
        text: data.title || '',
        style: {
          fontFamily: fontFamily,
          color: greyColor,
          fontWeight: '600'
        },
      },
      xAxis: {
        categories: data.x || [],
        title: {
          text: data.x_axis || null,
          style: {
            fontFamily: fontFamily,
            color: greyColor
          },
        },
        labels: {
          style: {
            fontFamily: fontFamily,
            color: greyColor
          },
        }
      },
      yAxis: {
        min: 0,
        title: {
          text: data.y_axis || null,
          align: 'high',
          style: {
            fontFamily: fontFamily,
            color: greyColor
          },
        },
        labels: {
          formatter: function () {
            const value = formatNumber(this.value);
            return data.y_is_percentage ? `${value}%` : value;
          },
          style: {
            fontFamily: fontFamily,
            color: greyColor
          },
        }
      },
      legend: {
        itemStyle: {
          fontFamily: fontFamily,
          color: greyColor
        }
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          const headerKey = (this.points && this.points.length) ? this.points[0].key : this.x;
          const header = `<div style="font-size:12.8px;font-weight:600;margin-bottom:6px;">${headerKey}</div>`;
          const rows = (this.points || []).map((point: any) => {
            const value = formatValue(point.y, point.series.userOptions.unit || '');
            return `<div style="padding:3px 0; font-size: 12.8px;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background-color:${point.series.color};margin-right:6px;"></span><span style="color:#4E5258">${point.series.name}</span>: <span style="font-weight: 600">${value}</span></div>`;
          }).join('');
          return header + rows;
        },
        style: {
          fontFamily: fontFamily,
          minWidth: '120px',
        },
      },
      plotOptions: {
        series: {
          animation: false,
        },
        bar: {
          dataLabels: {
            enabled: true,
            formatter: function () {
              return formatValue(this.y, (this.series.userOptions as any).unit || '');
            },
            style: {
               fontFamily: fontFamily,
              color: greyColor,
              fontWeight: '600',
              textOutline: '1.5px contrast',
              },
          }
        }
      },
      credits: {
        enabled: false,
      },
      colors: this.colors,
      series: (data.series || []).map((s: any) => ({
        name: s.name,
        data: s.y,
        unit: s.unit || '',
        marker: {enabled: false, symbol: 'circle', radius: 5, states: {hover: {enabled: true}}},
        style: {
          fontFamily: fontFamily,
          color: greyColor
        },
      }))
    };
  }
}
